import React, { useState, useRef } from 'react';
import Slider from 'react-slick';
import { PostImage, isVideoMedia } from '../../../models/feed';
import { VideoPlayer } from './VideoPlayer';
import styles from './MediaGallery.module.scss';

interface MediaGalleryProps {
    items: PostImage[];
    height?: number;
    showPagination?: boolean;
    initialIndex?: number;
}

/**
 * SliderArrow Component
 * Custom arrow component for react-slick
 */
interface ArrowProps {
    onClick?: () => void;
    direction: 'left' | 'right';
}

const SliderArrow: React.FC<ArrowProps> = ({ onClick, direction }) => (
    <button
        className={`${styles.sliderArrow} ${
            direction === 'left' ? styles.arrowLeft : styles.arrowRight
        }`}
        onClick={onClick}
        aria-label={direction === 'left' ? 'Previous' : 'Next'}
    >
        <i className={`chevron ${direction} icon`} />
    </button>
);

/**
 * MediaGallery Component
 * A horizontal swipeable gallery for mixed image/video content
 */
export const MediaGallery: React.FC<MediaGalleryProps> = ({
    items,
    height = 400,
    showPagination = true,
    initialIndex = 0,
}) => {
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const sliderRef = useRef<Slider>(null);

    // If no items, render nothing
    if (items.length === 0) {
        return null;
    }

    // If only one item, render directly without carousel
    if (items.length === 1) {
        const item = items[0];
        const isVideo = isVideoMedia(item.mediaType);

        return (
            <div className={styles.singleItemContainer}>
                {isVideo ? (
                    <VideoPlayer
                        videoUrl={item.imageUrl}
                        thumbnailUrl={item.thumbnailUrl}
                        duration={item.duration}
                        height={height}
                    />
                ) : (
                    <div
                        className={styles.imageContainer}
                        style={{
                            height,
                            backgroundImage: `url(${item.imageUrl})`,
                        }}
                    >
                        <img
                            src={item.imageUrl}
                            alt={item.caption || 'Post image'}
                            className={styles.image}
                        />
                    </div>
                )}
            </div>
        );
    }

    const settings = {
        dots: showPagination,
        infinite: false,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: initialIndex,
        prevArrow: <SliderArrow direction="left" />,
        nextArrow: <SliderArrow direction="right" />,
        afterChange: (current: number) => setActiveIndex(current),
        customPaging: (i: number) => (
            <button
                className={`${styles.dot} ${
                    i === activeIndex ? styles.dotActive : ''
                }`}
                aria-label={`Go to slide ${i + 1} of ${items.length}`}
            />
        ),
        dotsClass: styles.dotsContainer,
    };

    return (
        <div className={styles.container}>
            <Slider ref={sliderRef} {...settings}>
                {items.map((item, index) => {
                    const isVideo = isVideoMedia(item.mediaType);

                    return (
                        <div key={item.id || index} className={styles.slide}>
                            {isVideo ? (
                                <VideoPlayer
                                    videoUrl={item.imageUrl}
                                    thumbnailUrl={item.thumbnailUrl}
                                    duration={item.duration}
                                    height={height}
                                />
                            ) : (
                                <div
                                    className={styles.imageContainer}
                                    style={{
                                        height,
                                        backgroundImage: `url(${item.imageUrl})`,
                                    }}
                                >
                                    <img
                                        src={item.imageUrl}
                                        alt={item.caption || `Image ${index + 1}`}
                                        className={styles.image}
                                        onError={(e: any) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </Slider>
        </div>
    );
};
